import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as Lucide from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { PropertyCard } from '@/components/PropertyCard';
import { useBookVisit } from '@/context/BookVisitContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteData } from '@/context/SiteDataContext';
import { resolveMediaUrl } from '@/lib/api';
import { ArrowRight, Shield, MapPin, Wallet, Users, ChevronRight, ChevronLeft, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const whyChooseUsBase = [
  { titleKey: 'common.rera_registered', descEn: 'All our properties are C.G. RERA compliant ensuring legal security', descHi: 'हमारी सभी प्रॉपर्टीज़ C.G. RERA अनुपालित हैं, जो कानूनी सुरक्षा सुनिश्चित करती हैं', icon: Shield },
  { titleEn: 'Local Expertise', titleHi: 'स्थानीय विशेषज्ञता', descEn: 'Deep knowledge of Jamul-Bhilai region since 2018', descHi: '2018 से जामुल-भिलाई क्षेत्र की गहरी जानकारी', icon: MapPin },
  { titleEn: 'Transparent Pricing', titleHi: 'पारदर्शी कीमत', descEn: 'Clear pricing with full registry support and no hidden costs', descHi: 'पूरी रजिस्ट्री सहायता के साथ स्पष्ट कीमत, कोई छिपी हुई लागत नहीं', icon: Wallet },
  { titleEn: 'One Stop Solution', titleHi: 'एक ही जगह सब कुछ', descEn: 'Plots, houses, farmhouses and commercial — all in one place', descHi: 'प्लॉट्स, घर, फार्महाउस और कमर्शियल — सब एक जगह', icon: Users },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const { openModal } = useBookVisit();
  const { t, lang, pickLocale } = useLanguage();
  const { categories, siteSettings } = useSiteData();

  const heroSlides = (siteSettings?.hero_slides || []).filter((s) => s.is_active !== false);
  const slide = heroSlides[activeSlide] || heroSlides[0];

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/properties?featured=true`);
        setFeatured((data || []).slice(0, 8));
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingFeatured(false);
      }
    })();
  }, []);

  // Auto-advance hero slides
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const id = setInterval(() => setActiveSlide((i) => (i + 1) % heroSlides.length), 6000);
    return () => clearInterval(id);
  }, [heroSlides.length]);

  const siteTitle = lang === 'hi' && siteSettings?.site_title_hi ? siteSettings.site_title_hi : siteSettings?.site_title_en || 'Shree Bajrang Developers';
  const siteDesc = lang === 'hi' && siteSettings?.site_description_hi ? siteSettings.site_description_hi : siteSettings?.site_description_en || '';
  const ogImage = resolveMediaUrl(siteSettings?.default_share_image || (slide && slide.background_image));

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "Shree Bajrang Developers",
    "url": typeof window !== 'undefined' ? window.location.origin : '',
    "logo": "https://customer-assets.emergentagent.com/job_b771c267-6e6a-452f-b6b8-f219955187da/artifacts/a7yev6rp_Shree%20Bajrang%2055.png",
    "description": siteDesc,
    "telephone": siteSettings?.contact_phone,
    "address": { "@type": "PostalAddress", "addressLocality": "Jamul", "addressRegion": "Chhattisgarh", "addressCountry": "IN" },
    "areaServed": ["Jamul", "Bhilai", "Khedamara", "Shivpuri"],
  };

  return (
    <>
      <Helmet>
        <title>{siteTitle}</title>
        <meta name="description" content={siteDesc} />
        <meta name="keywords" content={siteSettings?.site_keywords || ''} />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDesc} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        <html lang={lang} />
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      </Helmet>

      <div data-testid="home-page">
        {/* HERO */}
        {slide && (
          <section className="relative min-h-[88vh] md:min-h-[92vh] flex items-end overflow-hidden bg-gray-900">
            {/* Background */}
            <picture>
              {slide.background_image_mobile && (
                <source media="(max-width: 768px)" srcSet={resolveMediaUrl(slide.background_image_mobile)} />
              )}
              {slide.background_image && (
                <img
                  src={resolveMediaUrl(slide.background_image)}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
            </picture>
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

            {/* Content */}
            <div className="relative z-10 w-full max-w-7xl mx-auto px-4 lg:px-8 py-16 md:py-24">
              <div className="max-w-3xl text-white">
                <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold border border-white/20 mb-5">
                  <Shield className="h-3.5 w-3.5" /> {t('common.rera_registered', 'RERA Registered')} · CGRERA100518A000114
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5">
                  {pickLocale(slide, 'heading', 'Premium Plots & Ready Homes')}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-6 max-w-2xl">
                  {pickLocale(slide, 'subheading', '')}
                </p>
                {(lang === 'hi' ? slide.bullets_hi : slide.bullets_en)?.length > 0 && (
                  <ul className="space-y-2 mb-8">
                    {(lang === 'hi' ? slide.bullets_hi : slide.bullets_en).map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/90">
                        <CheckCircle2 className="h-5 w-5 mt-0.5 text-amber-300 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap gap-3">
                  {(slide.cta_primary_text_en || slide.cta_primary_text_hi) && (
                    <Link to={slide.cta_primary_link || '/projects'}>
                      <Button size="lg" className="bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full px-7 shadow-xl">
                        {pickLocale(slide, 'cta_primary_text', t('cta.explore'))}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                  {(slide.cta_secondary_text_en || slide.cta_secondary_text_hi) && (
                    <Button
                      size="lg"
                      variant="outline"
                      className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white hover:text-gray-900 rounded-full px-7"
                      onClick={() => {
                        if (slide.cta_secondary_link?.startsWith('#')) openModal();
                        else if (slide.cta_secondary_link) window.location.href = slide.cta_secondary_link;
                      }}
                    >
                      {pickLocale(slide, 'cta_secondary_text', t('cta.book_visit'))}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {heroSlides.length > 1 && (
              <div className="absolute bottom-6 right-6 z-10 flex gap-2">
                <button
                  onClick={() => setActiveSlide((i) => (i - 1 + heroSlides.length) % heroSlides.length)}
                  className="w-10 h-10 rounded-full bg-white/15 backdrop-blur hover:bg-white/30 text-white flex items-center justify-center"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setActiveSlide((i) => (i + 1) % heroSlides.length)}
                  className="w-10 h-10 rounded-full bg-white/15 backdrop-blur hover:bg-white/30 text-white flex items-center justify-center"
                  aria-label="Next slide"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </section>
        )}

        {/* CATEGORIES */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block text-brand-orange font-semibold text-sm tracking-widest uppercase mb-2">
                {lang === 'hi' ? 'श्रेणियाँ' : 'Categories'}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
                {t('common.explore_categories', 'Explore Our Properties')}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {categories.map((cat) => {
                const Icon = Lucide[cat.icon] || Lucide.Building2;
                return (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-gray-900 shadow-md hover:shadow-2xl transition-all duration-500"
                    data-testid={`home-cat-${cat.slug}`}
                  >
                    {cat.thumbnail_url && (
                      <img
                        src={resolveMediaUrl(cat.thumbnail_url)}
                        alt={cat.name_en}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-3">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-heading text-2xl font-bold mb-1">{pickLocale(cat, 'name', cat.name_en)}</h3>
                      <p className="text-sm text-white/85 line-clamp-2 mb-3">{pickLocale(cat, 'short_description', cat.short_description_en) || ''}</p>
                      <span className="inline-flex items-center text-amber-300 font-medium text-sm group-hover:translate-x-1 transition-transform">
                        {t('cta.view_all', 'View All')} <ArrowRight className="h-4 w-4 ml-1" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* FEATURED PROPERTIES */}
        {(featured.length > 0 || loadingFeatured) && (
          <section className="py-16 md:py-20 bg-gradient-to-b from-orange-50/40 to-white">
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <div className="flex items-end justify-between mb-8 md:mb-12">
                <div>
                  <span className="inline-block text-brand-orange font-semibold text-sm tracking-widest uppercase mb-2">
                    {lang === 'hi' ? 'चुनिंदा' : 'Featured'}
                  </span>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
                    {t('common.featured_properties', 'Featured Properties')}
                  </h2>
                  <p className="text-gray-600 mt-2">{t('common.hand_picked', 'Hand-picked properties for you')}</p>
                </div>
                <Link to="/projects" className="hidden md:inline-flex items-center text-brand-orange hover:text-brand-rust font-medium">
                  {t('cta.view_all', 'View All')} <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
              {loadingFeatured ? (
                <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-brand-orange" /></div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                  {featured.map((p) => <PropertyCard key={p.id} property={p} />)}
                </div>
              )}
            </div>
          </section>
        )}

        {/* WHY CHOOSE US */}
        <section className="py-16 md:py-20 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block text-amber-300 font-semibold text-sm tracking-widest uppercase mb-2">
                {lang === 'hi' ? 'क्यों चुनें' : 'Trust'}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">
                {t('common.why_choose', 'Why Choose Shree Bajrang Developers')}
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {whyChooseUsBase.map((item, i) => {
                const Icon = item.icon;
                const title = lang === 'hi' ? (item.titleHi || t(item.titleKey)) : (item.titleEn || t(item.titleKey));
                const desc = lang === 'hi' ? item.descHi : item.descEn;
                return (
                  <div key={i} className="bg-white/5 rounded-2xl p-5 md:p-6 backdrop-blur border border-white/10 hover:border-amber-300/40 transition-colors">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-orange to-brand-rust flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{title}</h3>
                    <p className="text-white/70 text-sm">{desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-brand-rust to-brand-orange text-white">
          <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-3">
              {t('common.ready_to_find', 'Ready to Find Your Dream Property?')}
            </h2>
            <p className="text-white/90 mb-8">
              {t('common.free_consultation', 'Free consultation')} · {t('common.no_obligation', 'No obligation site visits')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button size="lg" onClick={() => openModal()} className="bg-white text-brand-rust hover:bg-amber-50 rounded-full px-7">
                {t('cta.book_visit', 'Book a Visit')}
              </Button>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-rust rounded-full px-7 bg-transparent">
                  {t('cta.enquire_now', 'Enquire Now')}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
